/**
 * @fileoverview Get the analytics traits for a user.
 *
 * To address a Node.js version incompatibility, this code was extracted directly from the
 * @blueshift-ui/analytics package rather than being imported.
 *
 * @see https://github.com/varsitytutors/blueshift-ui/blob/master/packages/analytics/src/helpers/get-user-traits/index.ts
 */

/**
 * Get the analytics traits for an authenticated user's role.
 * @param authenticatedUser - The authenticated user to get the analytics traits for.
 * @returns The analytics traits for the role.
 */
function getRoleTraits(authenticatedUser) {
  switch (authenticatedUser.role) {
    case 'client':
      return {
        role_id: authenticatedUser.client_id,
        role_uuid: authenticatedUser.client_uuid,
      };
    case 'student': {
      const student = authenticatedUser.students?.[0];
      return {
        role_id: student?.id ?? authenticatedUser.id,
        role_uuid: student?.uuid ?? authenticatedUser.user_id,
      };
    }
    case 'tutor':
      return {
        role_id: authenticatedUser.tutor_id,
        role_uuid: authenticatedUser.tutor_uuid,
      };
    default:
      return {
        role_id: authenticatedUser.id,
        role_uuid: authenticatedUser.user_id,
      };
  }
}

/**
 * Get the analytics traits for a student.
 * @param student - The student to get the analytics traits for.
 * @returns The analytics traits for the student.
 */
function getStudentTraits(student) {
  return {
    email: student.email,
    enablements:
      student.enablements?.map((enablement) => enablement.product.short_name) ??
      [],
    first_name: student.first_name,
    grade_id: student.grade_list_id,
    last_name: student.last_name,
    user_id: student.user_id,
    uuid: student.uuid,
  };
}

/**
 * Get the analytics traits for a user.
 * @param user - The user to get the analytics traits for. It can be a direct response from the /me
 * endpoint or the return from the `useAuthenticatedUser` hook.
 * @returns The analytics traits for the user.
 */
function getUserTraits(user) {
  if (!user) {
    return undefined;
  }

  /**
   * This allows us to use the return from both useAuthenticatedUser ({@link AuthenticatedUser}) and
   * the /me endpoint directly ({@link AuthenticatedUserResponse}).
   */
  const authenticatedUser =
    'entity' in user
      ? {
          ...user.entity,
          students: user.students,
          user_id: user.token?.user_id,
        }
      : user;

  return {
    ...getRoleTraits(authenticatedUser),
    email: authenticatedUser.email,
    first_name: authenticatedUser.first_name,
    id: authenticatedUser.id,
    jurisdiction_id: authenticatedUser.jurisdiction_id,
    last_name: authenticatedUser.last_name,
    product_state: authenticatedUser.product_state,
    role: authenticatedUser.role,
    sales_group_ids: authenticatedUser.sales_group_list,
    students: authenticatedUser.students?.map(getStudentTraits) ?? [],
    user_id: authenticatedUser.user_id,
  };
}

export default getUserTraits;
